import * as Sentry from '@sentry/browser';
import { JwtPayload, jwtDecode } from 'jwt-decode';

import { addHours, isAfter } from '~/services/TimeService';

import { GoogleTokenData } from '~/types/google';

import type {
  AuthAccessTokenOptions,
  AuthAdapter,
  AuthImpersonationRequest,
  AuthLogoutOptions,
  AuthNotice,
  AuthProfileUpdatePayload,
  AuthStartLoginOptions,
  AuthState,
} from '../types';

import SessionStore from './SessionStore';
import UserService from './UserService';
import { validateLegacyRole } from './roleValidation';

type LegacyLogoutOptions = AuthLogoutOptions;

const authNoticeStorageKey = 'authNotice';
const legacyExpiredSessionMessageStorageKey = 'showExpiredTokenMessage';

export class LegacyAuthAdapter implements AuthAdapter {
  private listeners = new Set<() => void>();

  private readonly sessionStore: SessionStore;

  private readonly userService: UserService;

  private sessionError: Error | null = null;

  private state: AuthState;

  constructor(endpoints?: { baseUri?: string }) {
    this.sessionStore = new SessionStore();
    this.userService = new UserService(endpoints ?? { baseUri: window.configs.AUTH_HOST }, false);
    this.state = this.computeState();

    this.sessionStore.addChangeListener(() => {
      this.notify();
    });

    void this.resolveSession(false);
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): AuthState {
    return this.state;
  }

  async startLogin(options?: AuthStartLoginOptions): Promise<void> {
    if (options?.credential) {
      await this.loginWithCredential(options.credential);
      return;
    }

    if (!window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN) {
      throw new Error('No login mechanism available');
    }

    const returnTo = encodeURIComponent(options?.returnTo || window.location.origin);
    window.location.href = `https://${window.configs.GOOGLE_AUTH_REDIRECT_DOMAIN}/google/auth/init?return_to=${returnTo}`;
  }

  async completeLoginIfNeeded(): Promise<void> {
    const windowParams = new URLSearchParams(window.location.search);
    const credential = windowParams.get('credential');

    if (!credential) {
      return;
    }

    windowParams.delete('credential');
    const cleanSearch = windowParams.toString();
    window.history.replaceState({}, '', window.location.pathname + (cleanSearch ? `?${cleanSearch}` : ''));

    if (window.configs.GOOGLE_LOGIN_ENABLED !== 'true') {
      throw new Error('Google login is currently disabled');
    }

    await this.startLogin({ credential });
  }

  async getAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    if (options?.impersonation) {
      return this.getImpersonationAccessToken(options.impersonation);
    }

    return this.getLegacyAccessToken(false);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    if (options?.impersonation) {
      return this.getImpersonationAccessToken(options.impersonation);
    }

    return this.getLegacyAccessToken(true);
  }

  async logout(options?: AuthLogoutOptions): Promise<void> {
    await this.performLogout(options);
  }

  isAuthenticated() {
    return !!this.userService.getToken();
  }

  async updateProfile(data: AuthProfileUpdatePayload): Promise<void> {
    await this.wrapCallback<void>((callback) => {
      this.userService.updateProfile(data, (error) => {
        callback(error ?? null);
      });
    });

    await this.resolveSession(true);
  }

  consumeNotice(): AuthNotice | null {
    const notice = localStorage.getItem(authNoticeStorageKey);
    localStorage.removeItem(authNoticeStorageKey);

    if (notice === 'session-expired') {
      return { type: 'session-expired' };
    }

    const legacyNotice = localStorage.getItem(legacyExpiredSessionMessageStorageKey);
    localStorage.removeItem(legacyExpiredSessionMessageStorageKey);

    if (legacyNotice === 'true') {
      return { type: 'session-expired' };
    }

    return null;
  }

  private async loginWithCredential(credential: string): Promise<void> {
    this.resetSession();

    const tokenData = jwtDecode<GoogleTokenData>(credential);
    const loginPayload = {
      id_google: tokenData.sub,
      email: tokenData.email,
      token_id: credential,
      givenName: tokenData.given_name,
      surname: tokenData.family_name,
    };

    await this.wrapCallback<void>((callback) => {
      this.userService.loginWithGoogle(loginPayload, (error) => {
        callback(error ?? null);
      });
    });

    await this.resolveSession(true);
  }

  private async resolveSession(force = false): Promise<void> {
    if (!force && (this.sessionError || !this.sessionStore.empty())) {
      return;
    }

    this.sessionStore.set({
      loading: true,
      account: null,
    });

    await this.wrapCallback<void>((done) => {
      this.userService.me((error, account) => {
        let sessionError = error;

        if (!sessionError) {
          sessionError = validateLegacyRole(account);
        }

        if (sessionError) {
          this.sessionError = sessionError;
          this.userService.delToken();
          this.sessionStore.set({
            loading: false,
            account: null,
          });
        } else {
          this.sessionError = null;
          this.sessionStore.set({
            loading: false,
            account,
          });
        }

        done(null);
      });
    });
  }

  private async getLegacyAccessToken(forceRefresh: boolean): Promise<string | null> {
    const token = this.userService.getToken();

    if (!token || token === 'undefined') {
      await this.logout({ reason: 'session-expired', reload: true });
      return null;
    }

    if (!forceRefresh && this.isLegacyTokenFresh(token)) {
      return token;
    }

    return this.refreshAccessToken(token);
  }

  private isLegacyTokenFresh(token: string): boolean {
    try {
      const decodedToken = jwtDecode<JwtPayload>(token);

      if (!decodedToken.exp) {
        return false;
      }

      // This auth service stores exp in milliseconds, so preserve the legacy
      // Date(exp) behavior instead of converting a standard JWT seconds value.
      return !isAfter(addHours(1), new Date(decodedToken.exp));
    } catch (_error) {
      return false;
    }
  }

  private async refreshAccessToken(token: string): Promise<string | null> {
    try {
      const response = await fetch(`${window.configs.AUTH_HOST}/api/refresh-token`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const body = await response.json();
      const accessToken = body?.access_token;

      if (!response.ok || !accessToken) {
        throw new Error(body?.message || 'Auth token refresh failed');
      }

      this.userService.setToken(accessToken);
      this.notify();

      return accessToken;
    } catch (error) {
      Sentry.captureException(new Error(`${(error as Error).message} Auth token refresh failed`));
      await this.logout({ reason: 'session-expired', reload: true });
      return null;
    }
  }

  private async getImpersonationAccessToken(request: AuthImpersonationRequest): Promise<string | null> {
    const adminAccessToken = await this.getLegacyAccessToken(false);

    if (!adminAccessToken) {
      throw new Error('You are not logged in.');
    }

    const response = await fetch(`${window.configs.AUTH_HOST}/api/obtain-token`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
        Authorization: `Bearer ${adminAccessToken}`,
      },
      body: JSON.stringify({
        customer_id: request.customerId,
        verbose: true,
        ...(request.brand ? { brand: request.brand } : {}),
      }),
    });

    const body = await response.json();

    if (!response.ok) {
      throw new Error(body?.message || 'Failed to obtain impersonation token');
    }

    return body?.result?.access_token ?? null;
  }

  private async performLogout(options: LegacyLogoutOptions = {}): Promise<void> {
    const { reason, reload = false } = options;
    let logoutError: Error | null = null;

    if (reason === 'session-expired') {
      localStorage.setItem(authNoticeStorageKey, reason);
    }

    try {
      await this.wrapCallback<void>((callback) => {
        this.userService.logout((error) => {
          callback(error ?? null);
        });
      });
    } catch (error) {
      logoutError = error as Error;
    }

    this.sessionError = null;
    this.sessionStore.set({
      loading: false,
      account: null,
    });
    this.notify();

    if (reload) {
      window.location.reload();
      return;
    }

    if (logoutError) {
      throw logoutError;
    }
  }

  private resetSession() {
    this.sessionError = null;
    this.sessionStore.set({
      loading: false,
      account: null,
    });
  }

  private notify() {
    this.state = this.computeState();
    this.listeners.forEach((listener) => listener());
  }

  private computeState(): AuthState {
    const session = this.sessionStore.get();
    const token = this.userService.getToken();

    return {
      loading: session?.loading ?? true,
      authenticated: !!token,
      token,
      user: session?.account ?? null,
    };
  }

  private wrapCallback<T>(operation: (callback: (error?: Error | null, result?: T) => void) => void): Promise<T> {
    return new Promise((resolve, reject) => {
      operation((error, result) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(result as T);
      });
    });
  }
}
